import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useWorkspaceLayout } from "@/context/LayoutContext";
import { quoteToForm, quoteToResult, isMultiQuote } from "@/lib/quoteEdit";
import CustomerSummary from "@/components/workspace/CustomerSummary";
import DiagnosticsDrawer from "@/components/workspace/DiagnosticsDrawer";
import ClassicWorkspace from "@/components/workspace/layouts/ClassicWorkspace";
import CommandWorkspace from "@/components/workspace/layouts/CommandWorkspace";
import LtlWorkspace from "@/components/workspace/layouts/LtlWorkspace";
import RepMotivation from "@/components/motivation/RepMotivation";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Loader2, Pencil } from "lucide-react";

const WS_KEY = "phs.ws.single.quoteId";

const initialForm = {
  customer_name: "", quote_ref: "", sales_rep: "", customer_type: "standard",
  product_id: "", quantity: 1, shipping_mode: "LTL", fedex_service_type: "FEDEX_FREIGHT_PRIORITY",
  declared_value: 0, origin_zip: "", destination_zip: "", origin_city: "", origin_state: "",
  dest_city: "", dest_state: "", pickup_location_type: "commercial",
  delivery_location_type: "commercial", pickup_date: "", notes: "",
  customer_address_id: "", dest_address1: "", dest_contact_name: "", dest_contact_phone: "", dest_contact_email: "",
  accessorials: {
    liftgate_pickup: false, liftgate_delivery: false, residential_pickup: false,
    residential_delivery: false, limited_access_pickup: false, limited_access_delivery: false,
    appointment_required: false, inside_delivery: false, notify_before_delivery: false,
    hazmat: false, over_dimensional: false,
  },
};

function deriveFinancial(rate, override) {
  if (!rate) return null;
  const base = { ...rate.pricing };
  if (override.price !== null && override.price !== "" && !isNaN(override.price)) {
    const sell = Number(override.price);
    base.sell_price = sell;
    base.is_override = true;
    base.margin_dollars = +(sell - rate.total_carrier_cost).toFixed(2);
    base.margin_pct = sell > 0 ? +((base.margin_dollars / sell) * 100).toFixed(2) : 0;
    base.margin_health = base.margin_pct >= 30 ? "green" : base.margin_pct >= base.min_margin_pct ? "yellow" : "red";
    base.needs_review = base.margin_health === "red";
  }
  return base;
}

export default function Workspace() {
  const { user } = useAuth();
  const { layout, setLayout } = useWorkspaceLayout();
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...initialForm, sales_rep: user?.name || "", ...(location.state?.prefill || {}) });
  const [products, setProducts] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [override, setOverride] = useState({ price: null, reason: "" });
  const [manualRate, setManualRate] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedQuoteId, setSavedQuoteId] = useState(null);
  const [savedQuoteNumber, setSavedQuoteNumber] = useState(null);
  const [editQuote, setEditQuote] = useState(null);
  const [editMulti, setEditMulti] = useState(false);
  const [pendingRate, setPendingRate] = useState(false);
  const appliedEditId = useRef(null);
  const editId = location.state?.editQuoteId || null;

  const [showCustomer, setShowCustomer] = useState(false);
  const [showDiag, setShowDiag] = useState(false);
  const [showExplain, setShowExplain] = useState(false);
  const [explainText, setExplainText] = useState("");
  const [explaining, setExplaining] = useState(false);

  useEffect(() => {
    api.get("/products", { params: { include_inactive: false } })
      .then((r) => setProducts(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  // Restore the last auto-saved quote when returning to the workspace (no re-rating).
  useEffect(() => {
    if (editId || location.state?.prefill) return;
    const storedId = sessionStorage.getItem(WS_KEY);
    if (!storedId) return;
    api.get(`/quotes/${storedId}`).then(({ data }) => {
      if (isMultiQuote(data)) return;
      setForm((f) => ({ ...f, ...quoteToForm(data) }));
      setResult(quoteToResult(data));
      setSelectedCode(data.selected_carrier_code || null);
      setSavedQuoteId(data.id);
      setSavedQuoteNumber(data.quote_number);
      setManualRate(data.manual_rate != null ? String(data.manual_rate) : "");
    }).catch(() => sessionStorage.removeItem(WS_KEY));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load a saved quote for re-editing (single-product quotes load here; multi quotes go to LTL).
  useEffect(() => {
    if (!editId || appliedEditId.current === editId) return;
    appliedEditId.current = editId;
    api.get(`/quotes/${editId}`).then(({ data }) => {
      const multi = isMultiQuote(data);
      setEditMulti(multi);
      setEditQuote(data);
      if (multi) {
        setLayout("ltl");
      } else {
        if (layout === "ltl") setLayout("command");
        setForm((f) => ({ ...f, ...quoteToForm(data) }));
        setSelectedCode(data.selected_carrier_code || null);
        setSavedQuoteId(data.id);
        setSavedQuoteNumber(data.quote_number);
        sessionStorage.setItem(WS_KEY, data.id);
        setPendingRate(true);
        toast.info(`Editing ${data.quote_number}`);
      }
    }).catch((e) => toast.error(apiError(e)));
  }, [editId]); // eslint-disable-line react-hooks/exhaustive-deps

  // After edit data is applied, auto-rate once so the rep sees the current comparison.
  useEffect(() => {
    if (!pendingRate) return;
    setPendingRate(false);
    getRates({ preferCarrier: editQuote?.selected_carrier_code });
  }, [pendingRate]); // eslint-disable-line react-hooks/exhaustive-deps

  const productName = useMemo(
    () => products.find((p) => p.id === form.product_id)?.name || "",
    [products, form.product_id],
  );

  const selectedRate = useMemo(
    () => result?.rates.find((r) => r.carrier_code === selectedCode) || null,
    [result, selectedCode],
  );

  const financial = useMemo(() => deriveFinancial(selectedRate, override), [selectedRate, override]);

  const buildPayload = () => ({
    ...form,
    quantity: Number(form.quantity) || 0,
    declared_value: Number(form.declared_value) || 0,
    manual_rate: manualRate ? Number(manualRate) : null,
  });

  const getRates = async (opts = {}) => {
    if (!form.product_id) return toast.error("Select a product first.");
    setLoading(true);
    try {
      const isNew = !savedQuoteId;
      const { data } = await api.post("/quotes/rate", {
        ...buildPayload(),
        quote_id: savedQuoteId || null,
        selected_carrier_code: opts.preferCarrier || null,
      });
      setResult(data);
      if (data.quote) {
        setSavedQuoteId(data.quote.id);
        setSavedQuoteNumber(data.quote.quote_number);
        sessionStorage.setItem(WS_KEY, data.quote.id);
      }
      const keep = opts.preferCarrier && data.rates?.some((r) => r.carrier_code === opts.preferCarrier);
      setSelectedCode(keep ? opts.preferCarrier : data.recommended_carrier_code || data.rates[0]?.carrier_code);
      setOverride({ price: null, reason: "" });
      const errs = (data.warnings || []).filter((w) => w.level === "error");
      if (errs.length) toast.warning(`${errs.length} blocking issue(s) — see warnings.`);
      else if (data.quote) toast.success(isNew ? `Rates calculated — saved as ${data.quote.quote_number}.` : `Rates updated on ${data.quote.quote_number}.`);
      else toast.success("Rates calculated.");
      if (isNew && data.quote) api.get("/motivation/hype?event=save").then((r) => { if (r.data?.text) toast(r.data.text); }).catch(() => {});
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Re-rate when manual rate entered and MANUAL selected
  const onManualRate = (v) => {
    setManualRate(v);
  };

  // Rating auto-saves the quote; Save now just confirms carrier selection, overrides & header edits.
  const saveQuote = async () => {
    if (!result || !savedQuoteId) return toast.error("Calculate rates first.");
    setSaving(true);
    try {
      const { data } = await api.put(`/quotes/${savedQuoteId}`, {
        selected_carrier_code: selectedCode,
        override_price: override.price !== null && override.price !== "" ? Number(override.price) : null,
        override_reason: override.reason,
        status: "quoted",
        customer_name: form.customer_name,
        quote_ref: form.quote_ref,
        sales_rep: form.sales_rep,
        notes: form.notes,
        customer_address_id: form.customer_address_id,
        dest_address1: form.dest_address1,
        dest_contact_name: form.dest_contact_name,
        dest_contact_phone: form.dest_contact_phone,
        dest_contact_email: form.dest_contact_email,
      });
      setSavedQuoteNumber(data.quote_number);
      toast.success(`Quote ${data.quote_number} updated.`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const newQuote = () => {
    sessionStorage.removeItem(WS_KEY);
    setForm({ ...initialForm, sales_rep: user?.name || "" });
    setResult(null);
    setSelectedCode(null);
    setOverride({ price: null, reason: "" });
    setManualRate("");
    setSavedQuoteId(null);
    setSavedQuoteNumber(null);
    setEditQuote(null);
    if (location.state) navigate("/workspace", { replace: true, state: null });
    toast.info("Started a new quote.");
  };

  const duplicateQuote = async () => {
    if (!savedQuoteId) return;
    try {
      const { data } = await api.post(`/quotes/${savedQuoteId}/duplicate`);
      setSavedQuoteId(data.id);
      setSavedQuoteNumber(data.quote_number);
      sessionStorage.setItem(WS_KEY, data.id);
      toast.success(`Duplicated as ${data.quote_number}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const explain = async () => {
    if (!result || !selectedRate) return toast.error("Calculate rates first.");
    setShowExplain(true);
    setExplaining(true);
    setExplainText("");
    try {
      const { data } = await api.post("/quotes/explain", {
        stack_result: result.stack_result,
        fak: result.fak,
        financial,
        recommended: selectedRate,
      });
      setExplainText(data.explanation);
    } catch (e) {
      setExplainText(apiError(e));
    } finally {
      setExplaining(false);
    }
  };

  const warnings = result?.warnings || [];

  const view = {
    form, setForm, products, productName, result, financial, selectedRate, selectedCode,
    loading, manualRate, savedQuoteId, savedQuoteNumber, saving, warnings,
    onGetRates: getRates,
    onNewQuote: newQuote,
    onSelect: setSelectedCode,
    onManualRate,
    onApplyOverride: (price, reason) => setOverride({ price, reason }),
    onClearOverride: () => setOverride({ price: null, reason: "" }),
    onSave: saveQuote,
    onDuplicate: duplicateQuote,
    onCustomer: () => setShowCustomer(true),
    onDiagnostics: () => setShowDiag(true),
    onExplain: explain,
  };

  return (
    <>
      <div className="mb-5">
        <RepMotivation />
      </div>
      {editQuote && !editMulti && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/10 px-3 py-2 text-sm text-[hsl(var(--primary))]" data-testid="editing-banner">
          <Pencil className="h-4 w-4" /> Editing quote <span className="font-bold">{editQuote.quote_number}</span> — change anything and re-rate; updates save to this quote automatically.
        </div>
      )}
      {layout === "ltl" ? (
        <LtlWorkspace products={products} salesRep={user?.name || ""} editQuote={editMulti ? editQuote : null} />
      ) : layout === "command" ? (
        <CommandWorkspace view={view} />
      ) : (
        <ClassicWorkspace view={view} />
      )}

      <CustomerSummary open={showCustomer} onOpenChange={setShowCustomer} form={form} result={result} financial={financial} />
      <DiagnosticsDrawer open={showDiag} onOpenChange={setShowDiag} result={result} selectedRate={selectedRate} />

      <Dialog open={showExplain} onOpenChange={setShowExplain}>
        <DialogContent className="max-w-lg" data-testid="explain-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <Sparkles className="h-5 w-5 text-[hsl(var(--primary))]" /> AI Quote Explanation
            </DialogTitle>
            <DialogDescription>Plain-English breakdown of this freight quote.</DialogDescription>
          </DialogHeader>
          {explaining ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Analyzing the freight quote…
            </div>
          ) : (
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{explainText}</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
